import React, { useState, useEffect, useMemo } from 'react';
import { Container, Row, Col, Card, Spinner, Alert, Button, ButtonGroup, Modal, Badge, ListGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import FunnelDiagram from '../../components/FunnelDiagram';
import { colors } from '../../brand-colors';

function WarRoom() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(0); // 0 = current month (index into months array)
  const [funnelData, setFunnelData] = useState(null);
  const [exitStrategy, setExitStrategy] = useState(null);
  const [capacityAnalysis, setCapacityAnalysis] = useState(null);
  const [topTask, setTopTask] = useState(null);

  // Holiday state
  const [holidays, setHolidays] = useState([]);
  const [featuredHoliday, setFeaturedHoliday] = useState(null);
  const [showHolidayModal, setShowHolidayModal] = useState(false);

  // Generate the next 12 months - define this early so useEffects can use it
  const months = useMemo(() => {
    const result = [];
    const today = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() + i, 1);
      result.push({
        index: i,
        label: date.toLocaleDateString('en-US', { month: 'short' }),
        fullLabel: date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
        startDate: date,
        endDate: new Date(date.getFullYear(), date.getMonth() + 1, 0),
      });
    }
    return result;
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  // Fetch holidays when selected month changes
  useEffect(() => {
    if (months.length > 0) {
      fetchHolidays();
    }
  }, [selectedMonth, months]);

  const fetchHolidays = async () => {
    if (!months[selectedMonth]) return;
    const monthDate = months[selectedMonth].startDate;
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth() + 1; // JS months are 0-indexed

    try {
      const response = await fetch(`/api/holidays/month/${year}/${month}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setHolidays(data.holidays || []);
        setFeaturedHoliday(data.featured || null);
      }
    } catch (err) {
      console.error('Error fetching holidays:', err);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [funnelRes, exitRes, capacityRes, taskRes] = await Promise.all([
        fetch('/api/marketing/funnel-diagram', { credentials: 'include' }),
        fetch('/api/exit-strategy/analysis', { credentials: 'include' }),
        fetch('/api/marketing/marketing-capacity-analysis', { credentials: 'include' }),
        fetch('/api/tasks/top', { credentials: 'include' })
      ]);

      if (funnelRes.ok) {
        const data = await funnelRes.json();
        setFunnelData(data);
      }

      if (exitRes.ok) {
        const data = await exitRes.json();
        setExitStrategy(data);
      }

      if (capacityRes.ok) {
        const data = await capacityRes.json();
        setCapacityAnalysis(data);
      }

      if (taskRes.ok) {
        const data = await taskRes.json();
        setTopTask(data.task);
      }
    } catch (err) {
      setError('Error loading data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const completeTopTask = async () => {
    if (!topTask) return;
    try {
      await fetch(`/api/tasks/update/${topTask.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'completed' })
      });
      // Refresh to get the next top task
      const taskRes = await fetch('/api/tasks/top', { credentials: 'include' });
      if (taskRes.ok) {
        const data = await taskRes.json();
        setTopTask(data.task);
      }
    } catch (err) {
      console.error('Error completing task:', err);
    }
  };

  // Get promotions for the selected month (excluding ended promotions)
  const monthPromotions = useMemo(() => {
    if (!funnelData?.promotions) return [];
    const month = months[selectedMonth];
    const now = new Date();
    return funnelData.promotions.filter(p => {
      const start = new Date(p.start_date);
      const end = new Date(p.end_date);
      // Only include if: starts in selected month AND hasn't ended yet
      return start >= month.startDate && start <= month.endDate && end >= now;
    });
  }, [funnelData, selectedMonth, months]);

  // Get content volume from capacity analysis
  const contentVolume = useMemo(() => {
    const recommendedTier = capacityAnalysis?.recommendation?.tier;
    const tierData = capacityAnalysis?.tier_analyses?.[recommendedTier]?.projection?.[0]?.content_volume;
    if (tierData) {
      return {
        social: tierData.total_social,
        videos: tierData.videos,
        emails: tierData.emails,
        articles: tierData.articles
      };
    }
    return null;
  }, [capacityAnalysis]);

  // Get sales targets for the SELECTED month from exit strategy projection
  const monthData = useMemo(() => {
    if (!exitStrategy?.monthly_plan) return null;
    // selectedMonth is 0-indexed (0 = current month = Month 1 in projection)
    const projectionMonth = selectedMonth + 1;
    const monthPlan = exitStrategy.monthly_plan.find(m => m.month === projectionMonth);
    if (!monthPlan) return null;

    // Use milestone data directly - no products sub-object
    return {
      month: projectionMonth,
      total_mrr: monthPlan.current_mrr || monthPlan.total_mrr || 0,
      total_customers: monthPlan.total_customers || 0,
      recurring_customers: monthPlan.recurring_customers || 0,
      one_time_customers: monthPlan.one_time_customers || 0,
      // These come directly from the milestone
      entry_acquisitions: monthPlan.entry_acquisitions || {},
      promo_notes: monthPlan.promo_notes || [],
      upgrade_notes: monthPlan.upgrade_notes || [],
      // New breakdown fields
      new_from_promos: monthPlan.new_from_promos || 0,
      new_from_organic: monthPlan.new_from_organic || 0,
      new_from_upgrades: monthPlan.new_from_upgrades || 0,
    };
  }, [exitStrategy, selectedMonth]);

  const selectedMonthData = months[selectedMonth];
  const formatDate = (d) => d.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" style={{ color: colors.goldMid }} />
        <p className="mt-3">Loading your War Room...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-4">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4 px-4">
      {/* Header */}
      <div className="mb-4 d-flex justify-content-between align-items-start">
        <div>
          <h1>War Room</h1>
          <p className="text-muted mb-0">
            Your 12-month battle plan. Select a month to see your traffic, products, and revenue targets.
          </p>
        </div>
        <Button
          variant="outline-primary"
          onClick={() => navigate('/command-center/campaign-hq/promotions')}
        >
          + Add Promotion
        </Button>
      </div>

      {/* Top Priority Business Task - shown first */}
      {topTask && (
        <Card className="mb-4 shadow-sm" style={{ borderLeft: `4px solid ${topTask.priority === 'high' ? '#dc3545' : colors.goldMid}` }}>
          <Card.Body className="py-3">
            <Row className="align-items-center">
              <Col xs="auto">
                <div
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '8px',
                    backgroundColor: topTask.priority === 'high' ? '#dc354520' : colors.goldLight + '40',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.5rem'
                  }}
                >
                  {topTask.priority === 'high' ? '🔥' : '📋'}
                </div>
              </Col>
              <Col>
                <div className="d-flex align-items-center">
                  <span className="text-muted small me-2">TOP PRIORITY TASK</span>
                  {topTask.priority === 'high' && (
                    <span className="badge bg-danger" style={{ fontSize: '0.65rem' }}>HIGH</span>
                  )}
                  {topTask.days_old > 30 && (
                    <span className="badge bg-warning text-dark ms-1" style={{ fontSize: '0.65rem' }}>
                      {topTask.days_old} days old
                    </span>
                  )}
                </div>
                <div className="fw-bold" style={{ fontSize: '1.1rem', color: colors.greyDarkest }}>
                  {topTask.title}
                </div>
                {topTask.description && (
                  <div className="text-muted small">{topTask.description}</div>
                )}
              </Col>
              <Col xs="auto">
                {topTask.source === 'playbook_review' && (
                  <Button
                    variant="outline-dark"
                    size="sm"
                    onClick={() => window.location.href = '/command-center/brand-playbook'}
                    className="me-2"
                  >
                    Open Playbook
                  </Button>
                )}
                <Button
                  variant="outline-success"
                  size="sm"
                  onClick={completeTopTask}
                  className="me-2"
                >
                  ✓ Done
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => window.location.href = '/command-center/calendars'}
                >
                  View All
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* Holiday Opportunities Section Header */}
      {(featuredHoliday || holidays.length > 0) && (
        <Card className="mb-2 shadow-sm">
          <Card.Header
            className="py-2"
            style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}
          >
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0" style={{ fontSize: '1rem' }}>
                🎯 {selectedMonthData.fullLabel} - Promotional Opportunities
              </h5>
              <span style={{ fontSize: '0.85rem', opacity: 0.8 }}>
                {holidays.filter(h => !h.has_promotion).length} available
              </span>
            </div>
          </Card.Header>
        </Card>
      )}

      {/* Featured Holiday / Promotional Opportunity - only show if there's a holiday without a promotion */}
      {featuredHoliday && !featuredHoliday.has_promotion && (
        <Card className="mb-4 shadow-sm" style={{ borderLeft: `4px solid ${colors.goldMid}` }}>
          <Card.Body className="py-3">
            <Row className="align-items-center">
              <Col xs="auto">
                <div
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '8px',
                    backgroundColor: colors.goldLight + '40',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.5rem'
                  }}
                >
                  🎯
                </div>
              </Col>
              <Col>
                <div className="d-flex align-items-center">
                  <span className="text-muted small me-2">FEATURED HOLIDAY</span>
                  <Badge bg={featuredHoliday.tier === 1 ? 'primary' : featuredHoliday.tier === 2 ? 'secondary' : 'light'} text={featuredHoliday.tier === 3 ? 'dark' : undefined}>
                    {featuredHoliday.tier === 1 ? 'Major' : featuredHoliday.tier === 2 ? 'Notable' : 'Niche'}
                  </Badge>
                </div>
                <div className="fw-bold" style={{ fontSize: '1.1rem', color: colors.greyDarkest }}>
                  {featuredHoliday.name} - {new Date(featuredHoliday.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                </div>
                <div className="text-muted small">{featuredHoliday.tagline}</div>
              </Col>
              <Col xs="auto">
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setShowHolidayModal(true)}
                  >
                    See All Holidays
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => navigate(`/command-center/campaign-hq/promotions?holiday=${featuredHoliday.id}&date=${featuredHoliday.date}`)}
                  >
                    Create Promotion
                  </Button>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* Month Selector */}
      <Card className="mb-4 shadow-sm">
        <Card.Body className="p-2">
          <div className="d-flex justify-content-center flex-wrap">
            <ButtonGroup>
              {months.map((month) => (
                <Button
                  key={month.index}
                  variant={selectedMonth === month.index ? 'dark' : 'outline-secondary'}
                  onClick={() => setSelectedMonth(month.index)}
                  style={{
                    minWidth: '70px',
                    fontWeight: selectedMonth === month.index ? '600' : '400',
                    backgroundColor: selectedMonth === month.index ? colors.greyDarkest : 'transparent',
                    borderColor: selectedMonth === month.index ? colors.greyDarkest : colors.greyLight,
                    color: selectedMonth === month.index ? colors.goldLight : colors.greyDark,
                  }}
                >
                  {month.label}
                  {month.index === 0 && <span className="ms-1" style={{ fontSize: '0.7rem' }}>●</span>}
                </Button>
              ))}
            </ButtonGroup>
          </div>
        </Card.Body>
      </Card>

      {/* Funnel Diagram */}
      <Card className="mb-4 shadow">
        <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
          <h2 className="mb-0" style={{ fontSize: '1.25rem' }}>
            {selectedMonthData.fullLabel}: Traffic → Products → Revenue
            <span className="float-end" style={{ fontSize: '0.9rem', opacity: 0.8 }}>
              {formatDate(selectedMonthData.startDate)} - {formatDate(selectedMonthData.endDate)}
            </span>
          </h2>
        </Card.Header>
        <Card.Body className="p-0">
          {funnelData ? (
            <FunnelDiagram
              products={funnelData.products}
              upgradePaths={funnelData.upgrade_paths}
              promotions={monthPromotions}
              trafficSources={funnelData.traffic_sources}
              contentVolume={contentVolume}
              salesTargets={monthData ? { entry_acquisitions: monthData.entry_acquisitions } : null}
            />
          ) : (
            <div className="text-center py-5 text-muted">
              <p>No product data available. Complete your product catalog first.</p>
            </div>
          )}
        </Card.Body>
        <Card.Footer className="bg-light d-flex justify-content-between align-items-center">
          <small className="text-muted">
            Traffic flows from marketing channels → entry products → upgrades through your value ladder.
            {monthPromotions.length > 0 && (
              <span className="text-danger ms-2">
                <strong>{monthPromotions.length} promotion(s)</strong> scheduled!
              </span>
            )}
          </small>
          {monthData && (
            <span style={{ color: colors.goldDark, fontWeight: '600' }}>
              Target MRR: ${monthData.total_mrr?.toLocaleString(undefined, {maximumFractionDigits: 0}) || '0'}
            </span>
          )}
        </Card.Footer>
      </Card>

      {/* Month Targets Summary */}
      <Row className="mb-4">
        {/* Scheduled Promotions */}
        <Col md={4} className="mb-3">
          <Card className="h-100 shadow-sm" style={{ borderTop: `3px solid ${colors.goldMid}` }}>
            <Card.Body>
              <h5 style={{ color: colors.goldDark }}>
                <span className="me-2">🎯</span>
                Scheduled Promotions
              </h5>
              {monthData?.promo_notes && monthData.promo_notes.length > 0 ? (
                <ul className="mb-0 small">
                  {monthData.promo_notes.map((note, index) => (
                    <li key={index} className="mb-2">{note}</li>
                  ))}
                </ul>
              ) : monthPromotions.length > 0 ? (
                <ul className="mb-0">
                  {monthPromotions.map((promo, index) => (
                    <li key={index} className="mb-2">
                      <strong>{promo.promotion_name}</strong>
                      <br />
                      <small className="text-muted">
                        <strong>{promo.estimated_sales || 0}</strong> expected sales @ ${parseFloat(promo.sale_price || 0).toLocaleString()}
                      </small>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted mb-0">No promotions scheduled this month</p>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Organic Acquisitions */}
        <Col md={4} className="mb-3">
          <Card className="h-100 shadow-sm" style={{ borderTop: '3px solid #28a745' }}>
            <Card.Body>
              <h5 className="text-success">
                <span className="me-2">🌱</span>
                Organic Acquisition Targets
              </h5>
              {monthData?.entry_acquisitions && Object.keys(monthData.entry_acquisitions).length > 0 ? (
                <ul className="mb-0">
                  {Object.entries(monthData.entry_acquisitions).map(([productName, count], index) => (
                    <li key={index} className="mb-2">
                      <strong>{count}</strong> new customers for <strong>{productName}</strong>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted mb-0">No organic targets for this month</p>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Expected Upgrades */}
        <Col md={4} className="mb-3">
          <Card className="h-100 shadow-sm" style={{ borderTop: '3px solid #0dcaf0' }}>
            <Card.Body>
              <h5 className="text-info">
                <span className="me-2">⬆️</span>
                Expected Upgrades
              </h5>
              {monthData?.upgrade_notes && monthData.upgrade_notes.length > 0 ? (
                <ul className="mb-0 small">
                  {monthData.upgrade_notes.map((note, index) => (
                    <li key={index} className="mb-1">{note}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted mb-0">No upgrades expected this month</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Month Revenue Summary */}
      {monthData && (
        <Card className="mb-4 shadow-sm" style={{ borderTop: `3px solid ${colors.redDarkest}` }}>
          <Card.Body>
            <Row className="text-center">
              <Col>
                <div className="fw-bold" style={{ fontSize: '1.75rem', color: colors.goldDark }}>
                  ${monthData.total_mrr?.toLocaleString(undefined, {maximumFractionDigits: 0}) || '0'}
                </div>
                <div className="text-muted">Target MRR</div>
              </Col>
              <Col>
                <div className="fw-bold" style={{ fontSize: '1.75rem', color: colors.greyDarkest }}>
                  {monthData.total_customers || 0}
                </div>
                <div className="text-muted">Total Customers</div>
                {(monthData.new_from_promos > 0 || monthData.new_from_organic > 0) && (
                  <small className="text-muted d-block" style={{ fontSize: '0.75rem' }}>
                    {monthData.new_from_promos > 0 && <span className="me-2">🎯{monthData.new_from_promos} promo</span>}
                    {monthData.new_from_organic > 0 && <span className="me-2">🌱{monthData.new_from_organic} organic</span>}
                    {monthData.new_from_upgrades > 0 && <span>⬆️{monthData.new_from_upgrades} upgrades</span>}
                  </small>
                )}
              </Col>
              <Col>
                <div className="fw-bold" style={{ fontSize: '1.75rem', color: '#28a745' }}>
                  {monthData.recurring_customers || 0}
                </div>
                <div className="text-muted">Recurring</div>
              </Col>
              <Col>
                <div className="fw-bold" style={{ fontSize: '1.75rem', color: '#6c757d' }}>
                  {monthData.one_time_customers || 0}
                </div>
                <div className="text-muted">One-Time</div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* Content Plan for Month */}
      {contentVolume && (
        <Card className="shadow-sm" style={{ borderTop: `3px solid ${colors.goldMid}` }}>
          <Card.Body>
            <h5 className="mb-3">
              <span className="me-2">📊</span>
              {selectedMonthData.fullLabel} Content Production
            </h5>
            <Row className="text-center">
              <Col>
                <div className="fw-bold" style={{ fontSize: '2rem', color: colors.goldDark }}>
                  {contentVolume.social}
                </div>
                <div className="text-muted">Social Posts</div>
              </Col>
              <Col>
                <div className="fw-bold" style={{ fontSize: '2rem', color: colors.goldDark }}>
                  {contentVolume.videos}
                </div>
                <div className="text-muted">Videos</div>
              </Col>
              <Col>
                <div className="fw-bold" style={{ fontSize: '2rem', color: colors.goldDark }}>
                  {contentVolume.emails}
                </div>
                <div className="text-muted">Emails</div>
              </Col>
              <Col>
                <div className="fw-bold" style={{ fontSize: '2rem', color: colors.goldDark }}>
                  {contentVolume.articles}
                </div>
                <div className="text-muted">Articles</div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* No Exit Strategy Warning */}
      {!exitStrategy?.monthly_plan && (
        <Alert variant="warning" className="mt-3">
          <Alert.Heading>Exit Strategy Not Set</Alert.Heading>
          <p className="mb-0">
            Complete your Exit Strategy to see month-by-month targets.
            The War Room uses your exit goals to calculate exactly what you need each month.
          </p>
        </Alert>
      )}

      {/* Holiday Picker Modal */}
      <Modal show={showHolidayModal} onHide={() => setShowHolidayModal(false)} size="lg">
        <Modal.Header closeButton style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
          <Modal.Title>
            🎯 Promotional Opportunities - {months[selectedMonth]?.fullLabel}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">
            Pick a holiday or event to build a promotion around. Major holidays have higher engagement,
            but niche days can work great for specific audiences.
          </p>

          <ListGroup>
            {holidays.map((holiday) => (
              <ListGroup.Item
                key={holiday.id}
                className="d-flex justify-content-between align-items-start"
                style={{
                  cursor: holiday.has_promotion ? 'default' : 'pointer',
                  opacity: holiday.has_promotion ? 0.6 : 1,
                  backgroundColor: holiday.has_promotion ? '#f8f9fa' : undefined
                }}
                onClick={() => {
                  if (holiday.has_promotion) return; // Don't navigate if already has promotion
                  setShowHolidayModal(false);
                  navigate(`/command-center/campaign-hq/promotions?holiday=${holiday.id}&date=${holiday.date}&name=${encodeURIComponent(holiday.name)}`);
                }}
              >
                <div className="ms-2 me-auto">
                  <div className="d-flex align-items-center mb-1">
                    <strong>{holiday.name}</strong>
                    <Badge
                      bg={holiday.tier === 1 ? 'primary' : holiday.tier === 2 ? 'secondary' : 'light'}
                      text={holiday.tier === 3 ? 'dark' : undefined}
                      className="ms-2"
                    >
                      {holiday.tier === 1 ? 'Major' : holiday.tier === 2 ? 'Notable' : 'Niche'}
                    </Badge>
                    {holiday.has_promotion && (
                      <Badge bg="success" className="ms-2">
                        ✓ Promotion Scheduled
                      </Badge>
                    )}
                  </div>
                  <div className="text-muted small">{holiday.tagline}</div>
                  {!holiday.has_promotion && holiday.promo_ideas && (
                    <div className="mt-1">
                      {holiday.promo_ideas.slice(0, 3).map((idea, i) => (
                        <Badge key={i} bg="light" text="dark" className="me-1" style={{ fontSize: '0.7rem' }}>
                          {idea}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                <div className="text-end">
                  <div className="fw-bold">{holiday.weekday}</div>
                  <div className="text-muted small">
                    {new Date(holiday.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>

          {holidays.length === 0 && (
            <Alert variant="info">
              No promotional opportunities found for this month.
            </Alert>
          )}

          {holidays.length > 0 && holidays.every(h => h.has_promotion) && (
            <Alert variant="success" className="mt-3">
              Great job! You have promotions scheduled for all holidays this month.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowHolidayModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default WarRoom;
